import {
  catchErrorInto,
  effect,
  exhaustMapWithTrailing,
  fromPromise,
  LiveData,
  onComplete,
  OnEvent,
  onStart,
  Service,
  smartRetry,
} from '@toeverything/infra';
import { filter, switchMap, tap, timer } from 'rxjs';

import { AccountChanged, type AuthService } from '../../cloud';
import { ServerStarted } from '../../cloud/events/server-started';
import { ApplicationFocused } from '../../lifecycle';
import type { NotificationStore } from '../stores/notification';

@OnEvent(ApplicationFocused, s => s.handleApplicationFocused)
@OnEvent(ServerStarted, s => s.handleServerStarted)
@OnEvent(AccountChanged, s => s.handleAccountChanged)
export class NotificationCountService extends Service {
  constructor(
    private readonly store: NotificationStore,
    private readonly authService: AuthService
  ) {
    super();
    this.listenToVisibilityChange();
  }

  loggedIn$ = this.authService.session.status$.map(v => v === 'authenticated');

  readonly count$ = LiveData.from(this.store.watchNotificationCountCache(), 0);
  readonly isLoading$ = new LiveData(false);
  readonly error$ = new LiveData<any>(null);

  revalidate = effect(
    switchMap(() => {
      return timer(0, 30000); // revalidate every 30 seconds
    }),
    // skip background polls while the page is hidden; the visibilitychange
    // listener triggers an immediate refresh when it becomes visible again
    filter(() => !isDocumentHidden()),
    exhaustMapWithTrailing(() => {
      return fromPromise(signal => {
        if (!this.loggedIn$.value) {
          return Promise.resolve(0);
        }
        return this.store.getNotificationCount(signal);
      }).pipe(
        tap(result => {
          this.setCount(result ?? 0);
        }),
        smartRetry(),
        catchErrorInto(this.error$),
        onStart(() => {
          this.isLoading$.setValue(true);
        }),
        onComplete(() => this.isLoading$.setValue(false))
      );
    })
  );

  handleApplicationFocused() {
    this.revalidate();
  }

  handleServerStarted() {
    this.revalidate();
  }

  handleAccountChanged() {
    this.revalidate();
  }

  setCount(count: number) {
    this.store.setNotificationCountCache(count);
  }

  private listenToVisibilityChange() {
    // document may be undefined under tests/Electron workers — feature-detect
    if (typeof document === 'undefined') {
      return;
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        this.revalidate();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    this.disposables.push(() =>
      document.removeEventListener('visibilitychange', onVisibilityChange)
    );
  }

  override dispose(): void {
    super.dispose();
    this.revalidate.unsubscribe();
  }
}

function isDocumentHidden() {
  return (
    typeof document !== 'undefined' && document.visibilityState === 'hidden'
  );
}
